import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Textarea,
} from '@nextui-org/react';
import { Edit3 } from 'lucide-react';
import forumService, {
  ForumPost,
  extractForumError,
} from '../../services/forumService';

const TEAL = '#3FCED2';

interface EditPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: ForumPost;
  onUpdated: (post: ForumPost) => void;
}

/**
 * Post-Bearbeiten-Modal (Port von forum_post_edit_modal.dart).
 */
const EditPostModal: React.FC<EditPostModalProps> = ({
  isOpen,
  onClose,
  post,
  onUpdated,
}) => {
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setContent(forumService.extractPlainText(post.content));
      setError(null);
    }
  }, [isOpen, post]);

  const handleSubmit = async () => {
    setError(null);
    const trimmed = content.trim();
    if (trimmed.length < 3) {
      setError('Bitte mindestens 3 Zeichen eingeben.');
      return;
    }
    const postId = post.id || post._id;
    if (!postId) {
      setError('Post-ID fehlt.');
      return;
    }
    setSubmitting(true);
    try {
      const updated = await forumService.updatePost(postId, {
        content: trimmed,
      });
      onClose();
      onUpdated(updated);
    } catch (err) {
      setError(extractForumError(err, 'Beitrag konnte nicht gespeichert werden'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-zinc-900 border border-zinc-800 text-white',
        closeButton: 'text-gray-400 hover:bg-zinc-800',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <Edit3 size={20} style={{ color: TEAL }} />
          Beitrag bearbeiten
        </ModalHeader>
        <ModalBody>
          <Textarea
            value={content}
            onValueChange={setContent}
            minRows={4}
            maxLength={10000}
            placeholder="Dein Beitrag..."
            classNames={{
              inputWrapper: 'bg-zinc-800 data-[hover=true]:bg-zinc-800',
              input: 'text-white',
            }}
          />
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="flat"
            className="bg-zinc-800 text-gray-300"
            onPress={onClose}
            isDisabled={submitting}
          >
            Abbrechen
          </Button>
          <Button
            style={{ backgroundColor: TEAL, color: '#000' }}
            className="font-semibold"
            onPress={handleSubmit}
            isLoading={submitting}
          >
            Speichern
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditPostModal;
